import AdminQuoteExtras from "./AdminQuoteExtras";
import React, { useState, useMemo } from "react";
import {
  LayoutDashboard,
  Search,
  Trash2,
  Mail,
  Phone,
  Home,
  Briefcase,
  Building2,
  Filter,
  ChevronDown,
  ChevronUp,
  Star,
  MailCheck,
  MailX,
} from "lucide-react";
import {
  filterQuotes,
  sortQuotes,
  formatDate,
  STATUS_CONFIG,
  PROPERTY_LABELS,
  PROPERTY_ICON_MAP,
} from "../../utils/quoteUtils";

/* ── Small Presentational Components ── */

const StatusBadge = ({ status }) => {
  const config = STATUS_CONFIG[status] || STATUS_CONFIG.pending;
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border ${config.color}`}
    >
      <span className={`w-1.5 h-1.5 rounded-full ${config.dotColor}`} />
      {config.label}
    </span>
  );
};

const SortIndicator = ({ active, direction }) => {
  if (!active) return null;
  return direction === "asc" ? (
    <ChevronUp className="w-3.5 h-3.5 inline ml-1" />
  ) : (
    <ChevronDown className="w-3.5 h-3.5 inline ml-1" />
  );
};

const EmptyState = ({ hasFilters }) => (
  <div className="p-12 text-center">
    <LayoutDashboard className="w-12 h-12 text-slate-300 mx-auto mb-3" />
    <p className="text-slate-500 font-medium">No quotes found</p>
    <p className="text-sm text-slate-400 mt-1">
      {hasFilters ? "Try adjusting your filters" : "Quote requests will appear here"}
    </p>
  </div>
);

/* ── Search & Filter Bar ── */

const QuoteFilters = ({ searchTerm, onSearchChange, statusFilter, onStatusChange, shownCount, totalCount }) => (
  <div className="bg-white rounded-xl border border-slate-200 p-4">
    <div className="flex flex-col sm:flex-row gap-4">
      <div className="relative flex-1">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <input
          type="text"
          placeholder="Search by name, email, phone, or service..."
          value={searchTerm}
          onChange={(e) => onSearchChange(e.target.value)}
          className="w-full pl-10 pr-4 py-2.5 rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-sky-600 focus:border-transparent text-sm"
        />
      </div>
      <div className="flex items-center gap-2">
        <Filter className="w-4 h-4 text-slate-400" />
        <select
          value={statusFilter}
          onChange={(e) => onStatusChange(e.target.value)}
          className="px-3 py-2.5 rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-sky-600 text-sm bg-white"
        >
          <option value="all">All Status</option>
          <option value="pending">Pending</option>
          <option value="contacted">Contacted</option>
          <option value="booked">Booked</option>
          <option value="completed">Completed</option>
          <option value="cancelled">Cancelled</option>
        </select>
      </div>
    </div>
    <div className="mt-3 text-xs text-slate-400">
      Showing {shownCount} of {totalCount} quotes
    </div>
  </div>
);

/* ── Expanded Row Detail ── */

const ICON_COMPONENTS = { Building2, Briefcase, Home };

const PropertyIcon = ({ propertyType }) => {
  const iconName = PROPERTY_ICON_MAP[propertyType] || "Home";
  const Icon = ICON_COMPONENTS[iconName];
  return <Icon className="w-4 h-4 text-slate-400" />;
};

const ExpandedQuoteRow = ({ quote }) => (
  <tr className="bg-sky-50/50">
    <td colSpan={7} className="px-6 py-5">
      <div className="grid md:grid-cols-3 gap-6">
        <div className="space-y-3">
          <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
            Contact Info
          </h4>
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-sm text-slate-700">
              <Mail className="w-4 h-4 text-slate-400" />
              <a href={`mailto:${quote.email}`} className="hover:text-sky-600 transition-colors">
                {quote.email}
              </a>
            </div>
            <div className="flex items-center gap-2 text-sm text-slate-700">
              <Phone className="w-4 h-4 text-slate-400" />
              <a href={`tel:${quote.phone}`} className="hover:text-sky-600 transition-colors">
                {quote.phone}
              </a>
            </div>
            <div className="flex items-center gap-2 text-sm text-slate-700">
              <PropertyIcon propertyType={quote.propertyType} />
              {PROPERTY_LABELS[quote.propertyType] || quote.propertyType}
            </div>
          </div>
        </div>

        <div className="space-y-3">
          <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
            Service Details
          </h4>
          <div className="space-y-2 text-sm text-slate-700">
            <div className="flex items-center gap-2">
              <Star className="w-4 h-4 text-slate-400" />
              {quote.selectedService}
            </div>
            {quote.carpet && (
              <div data-testid={`admin-quote-carpet-${quote.id}`}>
                <span className="text-slate-500">Carpets:</span>{" "}
                <span className="font-medium">
                  {quote.carpet.rooms} {quote.carpet.rooms === 1 ? "room" : "rooms"}, {quote.carpet.livingRooms} living, {quote.carpet.stairsHallways} stairs/hall{quote.carpet.stainProtection ? ", stain/odour treatment" : ""}
                </span>
              </div>
            )}
            {!quote.laundry && !quote.carpet && (
              <div>
                <span className="text-slate-500">Bedrooms:</span>{" "}
                <span className="font-medium">{quote.bedrooms}</span>
              </div>
            )}
            <div>
              <span className="text-slate-500">Frequency:</span>{" "}
              <span className="font-medium">{quote.frequency}</span>
            </div>
            {quote.laundry && (
              <div data-testid={`admin-quote-laundry-${quote.id}`}>
                <span className="text-slate-500">Laundry:</span>{" "}
                <span className="font-medium">
                  {quote.laundry.kg} kg wash, {quote.laundry.ironing} ironing, {quote.laundry.duvets} {quote.laundry.duvets === 1 ? "duvet" : "duvets"}, {quote.laundry.suits} {quote.laundry.suits === 1 ? "suit" : "suits"}
                </span>
              </div>
            )}
            <div data-testid={`admin-quote-parking-${quote.id}`}>
              <span className="text-slate-500">Parking:</span>{" "}
              <span className="font-medium">{quote.parkingAvailable ? "Available" : "Not available / not stated"}</span>
            </div>
            <div>
              <span className="text-slate-500">Estimate:</span>{" "}
              <span className="font-bold text-sky-600">
                £{quote.estimatedPrice}/{quote.laundry ? "order" : quote.carpet ? "job" : "visit"}
              </span>
            </div>
          </div>
        </div>

        <div className="space-y-3">
          <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
            Message
          </h4>
          <p className="text-sm text-slate-600 leading-relaxed bg-white rounded-lg p-3 border border-slate-200 min-h-[60px]">
            {quote.message || "No additional details provided."}
          </p>
        </div>
      </div>
      <AdminQuoteExtras quote={quote} />
    </td>
  </tr>
);

/* ── Row Actions (status dropdown + delete) ── */

const RowActions = ({ quote, deleteConfirm, onStatusChange, onDeleteRequest, onDeleteConfirm, onDeleteCancel }) => (
  <div className="flex items-center justify-end gap-2">
    <select
      value={quote.status}
      onChange={(e) => onStatusChange(quote.id, e.target.value)}
      className="text-xs px-2 py-1.5 rounded-md border border-slate-200 bg-white focus:outline-none focus:ring-1 focus:ring-sky-600"
    >
      <option value="pending">Pending</option>
      <option value="contacted">Contacted</option>
          <option value="booked">Booked</option>
      <option value="completed">Completed</option>
      <option value="cancelled">Cancelled</option>
    </select>
    {deleteConfirm ? (
      <div className="flex items-center gap-1">
        <button
          onClick={onDeleteConfirm}
          className="text-xs px-2 py-1 bg-red-600 text-white rounded hover:bg-red-700 transition-colors"
        >
          Confirm
        </button>
        <button
          onClick={onDeleteCancel}
          className="text-xs px-2 py-1 bg-slate-200 text-slate-600 rounded hover:bg-slate-300 transition-colors"
        >
          Cancel
        </button>
      </div>
    ) : (
      <button
        onClick={onDeleteRequest}
        className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors"
        title="Delete quote"
      >
        <Trash2 className="w-4 h-4" />
      </button>
    )}
  </div>
);

/* ── Table Headers ── */

const TableHeaders = ({ sortField, sortDir, onToggleSort }) => {
  const sortableHeader = (label, field) => (
    <th
      className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider cursor-pointer hover:text-sky-600"
      onClick={() => onToggleSort(field)}
    >
      {label} <SortIndicator active={sortField === field} direction={sortDir} />
    </th>
  );

  const staticHeader = (label) => (
    <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">
      {label}
    </th>
  );

  return (
    <thead>
      <tr className="bg-slate-50 border-b border-slate-200">
        {sortableHeader("Date", "created_at")}
        {sortableHeader("Customer", "name")}
        {staticHeader("Service")}
        {sortableHeader("Price", "estimatedPrice")}
        {staticHeader("Email Sent")}
        {staticHeader("Status")}
        <th className="text-right px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">
          Actions
        </th>
      </tr>
    </thead>
  );
};

/* ── Quote Row ── */

const QuoteRow = ({ quote, isExpanded, onToggleExpand, deleteConfirm, onStatusChange, onDeleteRequest, onDeleteConfirm, onDeleteCancel }) => (
  <>
    <tr
      className="hover:bg-slate-50 transition-colors cursor-pointer"
      onClick={() => onToggleExpand(quote.id)}
    >
      <td className="px-4 py-3.5">
        <span className="text-sm text-slate-600">
          {formatDate(quote.created_at)}
        </span>
      </td>
      <td className="px-4 py-3.5">
        <p className="text-sm font-semibold text-slate-900">{quote.name}</p>
        <p className="text-xs text-slate-400">{quote.email}</p>
      </td>
      <td className="px-4 py-3.5">
        <span className="text-sm text-slate-700">{quote.selectedService}</span>
      </td>
      <td className="px-4 py-3.5">
        <span className="text-sm font-bold text-sky-600">
          £{quote.estimatedPrice}
        </span>
      </td>
      <td className="px-4 py-3.5">
        {quote.email_sent ? (
          <MailCheck className="w-4 h-4 text-green-500" />
        ) : (
          <MailX className="w-4 h-4 text-slate-300" />
        )}
      </td>
      <td className="px-4 py-3.5">
        <StatusBadge status={quote.status} />
      </td>
      <td className="px-4 py-3.5 text-right" onClick={(e) => e.stopPropagation()}>
        <RowActions
          quote={quote}
          deleteConfirm={deleteConfirm}
          onStatusChange={onStatusChange}
          onDeleteRequest={onDeleteRequest}
          onDeleteConfirm={onDeleteConfirm}
          onDeleteCancel={onDeleteCancel}
        />
      </td>
    </tr>
    {isExpanded && <ExpandedQuoteRow quote={quote} />}
  </>
);

/* ── Main Table Component ── */

const AdminQuoteTable = ({ quotes, totalCount, onStatusChange, onDelete }) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [sortField, setSortField] = useState("created_at");
  const [sortDir, setSortDir] = useState("desc");
  const [expandedRow, setExpandedRow] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  // Split complex memoization into simpler parts to reduce dependency count
  // Note: filterQuotes is an imported utility - stable reference
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const filtered = useMemo(
    () => filterQuotes(quotes, statusFilter, searchTerm),
    [quotes, statusFilter, searchTerm, filterQuotes]
  );

  // Note: sortQuotes is an imported utility - stable reference
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const filteredQuotes = useMemo(
    () => sortQuotes(filtered, sortField, sortDir),
    [filtered, sortField, sortDir, sortQuotes]
  );

  const toggleSort = (field) => {
    if (sortField === field) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      setSortDir("desc");
    }
  };

  const toggleExpand = (quoteId) => {
    setExpandedRow(expandedRow === quoteId ? null : quoteId);
  };

  const hasFilters = searchTerm || statusFilter !== "all";

  const handleDeleteConfirm = (quoteId) => {
    onDelete(quoteId);
    setDeleteConfirm(null);
  };

  return (
    <>
      <QuoteFilters
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        statusFilter={statusFilter}
        onStatusChange={setStatusFilter}
        shownCount={filteredQuotes.length}
        totalCount={totalCount}
      />

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        {filteredQuotes.length === 0 ? (
          <EmptyState hasFilters={hasFilters} />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <TableHeaders
                sortField={sortField}
                sortDir={sortDir}
                onToggleSort={toggleSort}
              />
              <tbody className="divide-y divide-slate-100">
                {filteredQuotes.map((quote) => (
                  <React.Fragment key={quote.id}>
                    <QuoteRow
                      quote={quote}
                      isExpanded={expandedRow === quote.id}
                      onToggleExpand={toggleExpand}
                      deleteConfirm={deleteConfirm === quote.id}
                      onStatusChange={onStatusChange}
                      onDeleteRequest={() => setDeleteConfirm(quote.id)}
                      onDeleteConfirm={() => handleDeleteConfirm(quote.id)}
                      onDeleteCancel={() => setDeleteConfirm(null)}
                    />
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
};

export default AdminQuoteTable;
